// DIDs — inbound numbers with a cascading "Route to" picker (kind → record).
import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Loader2, PhoneIncoming } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import RelationalPicker from "@/admin/components/RelationalPicker";

const ROUTE_KINDS = [
  { v: "queue",      label: "Inbound Queue",  refKind: "queues" },
  { v: "ivr",        label: "IVR Flow",       refKind: "ivr_flows" },
  { v: "time_condition", label: "Time Condition", refKind: "time_conditions" },
  { v: "voicemail",  label: "Voicemail Box",  refKind: "voicemail_boxes" },
  { v: "campaign",   label: "Campaign (blended)", refKind: "campaigns" },
  { v: "external",   label: "External number", refKind: null },
];
const COUNTRIES = ["IN","US","UK","AE","SG","AU"];

export default function DIDsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const { canWrite, canDelete } = useCurrentModulePerms();

  async function load() {
    setLoading(true);
    try { const r = await api.get("/admin/resources/dids"); setRows(r.data || []); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function startNew() { setForm({ name: "", description: "", is_active: true, number: "", country: "IN", route_to_kind: "queue", route_to_id: "", external_number: "", recording: "always" }); setEditing("new"); }
  function startEdit(r) {
    setForm({
      name: r.name, description: r.description||"", is_active: !!r.is_active,
      number: r.data?.number||"", country: r.data?.country||"IN",
      route_to_kind: r.data?.route_to_kind||"queue",
      route_to_id: r.data?.route_to_id||"",
      external_number: r.data?.external_number||"",
      recording: r.data?.recording||"always",
    });
    setEditing(r);
  }
  async function save() {
    if (!form.name?.trim() || !form.number?.trim()) { toast.error("Name and Number required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), description: form.description, is_active: !!form.is_active,
        data: {
          number: form.number.trim(), country: form.country,
          route_to_kind: form.route_to_kind,
          route_to_id: form.route_to_kind === "external" ? "" : (form.route_to_id || ""),
          external_number: form.route_to_kind === "external" ? form.external_number : "",
          recording: form.recording,
        },
      };
      if (editing === "new") await api.post("/admin/resources/dids", payload);
      else await api.patch(`/admin/resources/dids/${editing.id}`, payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }
  async function remove(r) {
    if (!confirm(`Delete DID "${r.name}"?`)) return;
    try { await api.delete(`/admin/resources/dids/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  const routeMeta = ROUTE_KINDS.find((k) => k.v === form.route_to_kind);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="dids-title">Inbound DIDs</h1>
          <p className="text-sm text-muted-foreground mt-1">Map each inbound number to a queue, IVR, voicemail box, or external destination.</p>
        </div>
        {canWrite && <Button data-testid="add-did-btn" onClick={startNew} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"><Plus className="w-4 h-4"/> Add DID</Button>}
      </div>
      <Card className="overflow-hidden">
        {loading ? <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground"><tr className="text-left">
              <th className="px-4 py-2">Label</th><th className="px-4 py-2">Number</th><th className="px-4 py-2">Country</th><th className="px-4 py-2">Routes to</th><th className="px-4 py-2">Recording</th><th className="px-4 py-2">Active</th><th className="px-4 py-2 w-24">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-muted/30" data-testid={`row-did-${r.id}`}>
                  <td className="px-4 py-2.5"><div className="flex items-center gap-2"><PhoneIncoming className="w-4 h-4 text-primary"/><span className="font-medium">{r.name}</span></div></td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.data?.number||"—"}</td>
                  <td className="px-4 py-2.5"><Badge variant="outline">{r.data?.country||"—"}</Badge></td>
                  <td className="px-4 py-2.5 text-xs">
                    <Badge variant="outline" className="mr-1 text-[10px] uppercase">{r.data?.route_to_kind||"—"}</Badge>
                    {r.data?.route_to_kind === "external" ? r.data?.external_number : (r.data?.route_to_id || "—")}
                  </td>
                  <td className="px-4 py-2.5 text-xs">{r.data?.recording||"—"}</td>
                  <td className="px-4 py-2.5">{r.is_active ? <Badge variant="outline" className="text-[10px]">On</Badge> : <Badge variant="destructive" className="text-[10px]">Off</Badge>}</td>
                  <td className="px-4 py-2.5"><div className="flex gap-1">
                    {canWrite && <Button data-testid={`edit-did-${r.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={()=>startEdit(r)}><Edit2 className="w-4 h-4"/></Button>}
                    {canDelete && <Button data-testid={`del-did-${r.id}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={()=>remove(r)}><Trash2 className="w-4 h-4"/></Button>}
                  </div></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-sm text-muted-foreground">No DIDs yet.</td></tr>}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={!!editing} onOpenChange={(v)=>!v&&setEditing(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing==="new"?"New DID":`Edit ${editing?.name}`}</DialogTitle><DialogDescription>Map this inbound number to its destination.</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 py-2">
            <div className="sm:col-span-2"><Label>Label</Label><Input data-testid="did-name" value={form.name||""} onChange={(e)=>setForm({...form, name:e.target.value})} placeholder="Main inbound — Bengaluru" /></div>
            <div><Label>Number (E.164)</Label><Input data-testid="did-number" value={form.number||""} onChange={(e)=>setForm({...form, number:e.target.value})} placeholder="+918047447000" /></div>
            <div><Label>Country</Label>
              <Select value={form.country} onValueChange={(v)=>setForm({...form, country:v})}>
                <SelectTrigger data-testid="did-country" className="h-9"><SelectValue/></SelectTrigger>
                <SelectContent>{COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Route to (kind)</Label>
              <Select value={form.route_to_kind} onValueChange={(v)=>setForm({...form, route_to_kind:v, route_to_id:"", external_number:""})}>
                <SelectTrigger data-testid="did-route-kind" className="h-9"><SelectValue/></SelectTrigger>
                <SelectContent>{ROUTE_KINDS.map(k => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              {routeMeta?.refKind ? (
                <>
                  <Label>Target</Label>
                  <RelationalPicker kind={routeMeta.refKind} value={form.route_to_id} onChange={(v)=>setForm({...form, route_to_id:v})} placeholder={`Choose ${routeMeta.label.toLowerCase()}…`} testId="did-route-target" />
                </>
              ) : routeMeta?.v === "external" ? (
                <>
                  <Label>External destination number</Label>
                  <Input data-testid="did-external" value={form.external_number||""} onChange={(e)=>setForm({...form, external_number:e.target.value})} placeholder="+44 20 7946 0958" />
                </>
              ) : null}
            </div>
            <div className="sm:col-span-2"><Label>Recording</Label>
              <Select value={form.recording} onValueChange={(v)=>setForm({...form, recording:v})}>
                <SelectTrigger data-testid="did-recording" className="h-9"><SelectValue/></SelectTrigger>
                <SelectContent>{["always","on_demand","off"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 sm:col-span-2"><Switch data-testid="did-active" checked={!!form.is_active} onCheckedChange={(v)=>setForm({...form, is_active:!!v})} /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setEditing(null)}>Cancel</Button><Button data-testid="did-save" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
